import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import {
  consumeSignupCreditAward,
  SIGNUP_CREDIT_AWARD_EVENT,
} from '../services/signupCreditAward';

import { SignupCreditAwardModal } from './SignupCreditAwardModal';

const SignupCreditAwardConsumer: React.FC = () => {
  const [amount, setAmount] = useState<number | null>(null);

  const consumeAward = useCallback(() => {
    const nextAmount = consumeSignupCreditAward();

    if (nextAmount !== null) setAmount(nextAmount);
  }, []);

  useEffect(() => {
    // Defer consumption so React StrictMode can complete its development-only
    // setup/cleanup replay without consuming and then discarding the award.
    const timer = window.setTimeout(consumeAward, 0);

    window.addEventListener(SIGNUP_CREDIT_AWARD_EVENT, consumeAward);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener(SIGNUP_CREDIT_AWARD_EVENT, consumeAward);
    };
  }, [consumeAward]);

  return <SignupCreditAwardModal amount={amount} onClose={() => setAmount(null)} />;
};

export const SignupCreditAwardBridge: React.FC = () => {
  const { pathname } = useLocation();
  const { isAuthenticated, isLoading } = useAuth();
  const isCompletingAuth = pathname.startsWith('/auth/callback/')
    || pathname === '/auth/verify-email';

  useEffect(() => {
    if (!isCompletingAuth && !isLoading && !isAuthenticated) {
      // Redirect query parameters are not trusted until /me has confirmed the
      // token pair. Discard any queued message when authentication fails.
      consumeSignupCreditAward();
    }
  }, [isAuthenticated, isCompletingAuth, isLoading]);

  return isCompletingAuth || isLoading || !isAuthenticated
    ? null
    : <SignupCreditAwardConsumer />;
};
